import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { MatDialog } from '@angular/material/dialog';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { Observable, forkJoin, of } from 'rxjs';
import { finalize, startWith, debounceTime, distinctUntilChanged, switchMap, map } from 'rxjs/operators';
import { showSpinner, hideSpinner } from 'src/app/core/services/functions.service';
import { AjusteValoresService, AjusteValor } from '../../services/ajuste-valores.service';
import { ConceptoComisionService, ConceptoComision } from '../../services/concepto-comision.service';
import { PeriodosmetasService, PeriodoMetas } from '../../../periodosmetas/services/periodosmetas.service';
import { VendedorService, Vendedor } from '../../../metavendedor/services/vendedor.service';
import { DetalleAjusteDialogComponent } from '../detalle-ajuste-dialog/detalle-ajuste-dialog.component';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { NotificationHelper } from 'src/app/core/services/helpers/notification.helper';
import { getEndpoint,getCurrentUser,} from 'src/app/core/services/functions.service';

@Component({
  selector: 'app-ajustedevalores',
  templateUrl: './ajustedevalores.component.html',
  styleUrls: ['./ajustedevalores.component.scss']
})
export class AjustedevaloresComponent implements OnInit {
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild('autoVendedor') autocompleteTrigger!: MatAutocompleteTrigger;

  ajusteForm: FormGroup;
  isLoading = false;
  isEditing = false;
  editingAjuste: AjusteValor | null = null;
  periodos: PeriodoMetas[] = [];
  periodosAbiertos: PeriodoMetas[] = [];
  conceptos: ConceptoComision[] = [];
  vendedores: Vendedor[] = [];
  private vendedoresCargados = false;
  vendedoresFiltrados!: Observable<Vendedor[]>;
  mostrarTodosVendedores = false;
  vendedorControl = new FormControl('');
  periodoFiltro: string = '';
  vendedorFilterControl = new FormControl('');
  vendedoresFiltradosFilter!: Observable<Vendedor[]>;
  mostrarTodosVendedoresFilter = false;

  displayedColumns: string[] = ['periodo', 'concepto', 'c_ven', 'valor', 'acciones'];
  dataSource = new MatTableDataSource<AjusteValor>([]);

  constructor(
    private fb: FormBuilder,
    private ajusteValoresService: AjusteValoresService,
    private conceptoComisionService: ConceptoComisionService,
    private periodosmetasService: PeriodosmetasService,
    private vendedorService: VendedorService,
    private dialog: MatDialog,
    private notificationService: NotificationHelper
    
  ) {
    this.initForm();
  }

  initForm(): void {
    this.ajusteForm = this.fb.group({
      periodo: ['', Validators.required],
      concepto: ['', Validators.required],
      vendedor: ['', Validators.required],
      valor: ['', [Validators.required]]
    });
  }

  ngOnInit(): void {
    // Inicializar el dataSource con un array vacío
    this.dataSource = new MatTableDataSource<AjusteValor>([]);
    this.cargarDatos();
    this.setupFormObservables();
  }

  ngAfterViewInit() {
    // Configurar el paginator y sort después de que la vista se haya inicializado
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  setupFormObservables(): void {
    // Configurar el observable de vendedores para el formulario
    this.vendedoresFiltrados = this.vendedorControl.valueChanges.pipe(
      startWith(''),
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(value => {
        if (this.mostrarTodosVendedores) {
          this.mostrarTodosVendedores = false;
          return this.cargarVendedores();
        }

        const searchTerm = typeof value === 'string' ? value : '';
        return this.cargarVendedores(searchTerm);
      })
    );

    // Configurar el observable de vendedores para el filtro
    this.vendedoresFiltradosFilter = this.vendedorFilterControl.valueChanges.pipe(
      startWith(''),
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(value => {
        if (this.mostrarTodosVendedoresFilter) {
          this.mostrarTodosVendedoresFilter = false;
          return this.cargarVendedores();
        }

        const searchTerm = typeof value === 'string' ? value : '';
        return this.cargarVendedores(searchTerm);
      })
    );

    this.vendedorControl.valueChanges.subscribe(value => {
      if (value && typeof value === 'object') {
        this.ajusteForm.get('vendedor')?.setValue(value.codigo_vendedor);
      }
    });
  }

  cargarDatos(): void {
    showSpinner();
    this.isLoading = true;

    // Cargar todos los datos necesarios usando forkJoin
    forkJoin({
      ajustes: this.ajusteValoresService.getAjustes('JA'),
      periodos: this.periodosmetasService.getAllPeriodos('JA'),
      periodosAbiertos: this.periodosmetasService.getPeriodosAbiertos('JA'),
      conceptos: this.conceptoComisionService.getConceptosAjuste('JA'),
      vendedores: this.vendedorService.getVendedores('JA')
    }).pipe(
      finalize(() => {
        this.isLoading = false;
        hideSpinner();
      })
    ).subscribe({
      next: (response) => {
        if (response.ajustes && response.ajustes.data) {
          this.dataSource.data = response.ajustes.data;
        }  
        
        // Manejar tanto array directo como objeto con data
        const periodosData = Array.isArray(response.periodos) ? response.periodos : 
                           (response.periodos && response.periodos.data ? response.periodos.data : []);
        
        if (periodosData.length > 0) {
          // Ordenar los periodos de más reciente a más antiguo
          this.periodos = periodosData.sort((a, b) => b.periodo.localeCompare(a.periodo));
        } else {
          this.notificationService.errorMessage('No se encontraron periodos', response.periodos);
        }
        
        if (response.periodosAbiertos && response.periodosAbiertos.data) {
          // Ordenar los períodos abiertos de más reciente a más antiguo
          this.periodosAbiertos = response.periodosAbiertos.data.sort((a, b) => b.periodo.localeCompare(a.periodo));
          
          // Si hay períodos abiertos, establecer el primero como valor por defecto en el formulario
          if (this.periodosAbiertos.length > 0) {
            this.ajusteForm.patchValue({
              periodo: this.periodosAbiertos[0].periodo
            });
          }
        }
        if (response.conceptos) {
          this.conceptos = response.conceptos;
        }
        if (response.vendedores) {
          this.vendedores = response.vendedores;
        }

        // Configurar el filtrado
        this.dataSource.filterPredicate = (data: AjusteValor, filter: string) => {
          const searchStr = filter.toLowerCase();
          
          // Aplicar filtro de período si está seleccionado
          if (this.periodoFiltro && data.periodo !== this.periodoFiltro) {
            return false;
          }
          
          // Aplicar filtro de búsqueda general
          const vendedor = this.vendedores.find(v => v.codigo_vendedor === data.c_ven);
          const concepto = this.conceptos.find(c => c.codigo === data.concepto);
          
          return data.periodo.toLowerCase().includes(searchStr) ||
                 (concepto?.descripcion || '').toLowerCase().includes(searchStr) ||
                 (vendedor?.nombre_vendedor || '').toLowerCase().includes(searchStr) ||
                 data.c_ven.toLowerCase().includes(searchStr);
        };

        this.notificationService.successMessage('Datos cargados correctamente');
      },
      error: (error) => {
        this.notificationService.errorMessage('Error al cargar los datos');
      }
    });
  }

  onSubmit(): void {
    if (this.ajusteForm.invalid) {
      return;
    }

    const usuario = getCurrentUser();
    if (!usuario || !usuario.c_emp || !usuario.usuario_aplicativo) {
      this.notificationService.errorMessage('No se pudo obtener la información completa del usuario actual');
      return;
    }

    this.isLoading = true;
    showSpinner(this.isEditing ? 'Actualizando registro...' : 'Guardando registro...');
    
    const formData = this.ajusteForm.value;

    // Si estamos editando, usar el c_ven del registro original
    const vendedorCodigo = this.isEditing && this.editingAjuste 
      ? this.editingAjuste.c_ven 
      : formData.vendedor;

    const ajuste: AjusteValor = {
      id: this.isEditing && this.editingAjuste ? this.editingAjuste.id : undefined,
      c_emp: usuario.c_emp,
      periodo: formData.periodo,
      concepto: formData.concepto,
      c_ven: vendedorCodigo,
      valor: this.parseValor(formData.valor),
      usuario_creacion: this.isEditing ? undefined : usuario.usuario_aplicativo,
      usuario_modificacion: this.isEditing ? usuario.usuario_aplicativo : undefined
    };

    const request = this.isEditing && this.editingAjuste?.id 
      ? this.ajusteValoresService.updateAjuste(this.editingAjuste.id, ajuste, 'JA')
      : this.ajusteValoresService.createAjuste(ajuste, 'JA');

    request.pipe(
      finalize(() => {
        this.isLoading = false;
        hideSpinner();
      })
    ).subscribe({
      next: () => {
        this.notificationService.successMessage(
          this.isEditing ? 'Ajuste actualizado correctamente' : 'Ajuste creado correctamente'
        );
        this.resetForm();
        this.cargarDatos();
      },
      error: (error) => {
        this.notificationService.errorMessage(error.error?.detail || 'Error al guardar el ajuste');
      }
    });
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

  private _filtrarVendedores(value: string): Vendedor[] {
    if (this.mostrarTodosVendedores) {
      this.mostrarTodosVendedores = false;
      return this.vendedores;
    }

    if (!value) {
      return [];
    }

    const filterValue = value.toLowerCase();
    return this.vendedores.filter(vendedor =>
      vendedor.nombre_vendedor.toLowerCase().includes(filterValue) ||
      vendedor.codigo_vendedor.toLowerCase().includes(filterValue)
    );
  }

  private _filtrarVendedoresFilter(value: string): Vendedor[] {
    if (this.mostrarTodosVendedoresFilter) {
      this.mostrarTodosVendedoresFilter = false;
      return this.vendedores;
    }

    if (!value) {
      return [];
    }

    const filterValue = value.toLowerCase();
    return this.vendedores.filter(vendedor =>
      vendedor.nombre_vendedor.toLowerCase().includes(filterValue) ||
      vendedor.codigo_vendedor.toLowerCase().includes(filterValue)
    );
  }

  applyPeriodoFilter(): void {
    this.dataSource.filterPredicate = (data: AjusteValor, filter: string) => {
      const searchStr = filter.toLowerCase();
      
      // Aplicar filtro de período si está seleccionado
      if (this.periodoFiltro && data.periodo !== this.periodoFiltro) {
        return false;
      }
      
      // Aplicar filtro de búsqueda general
      const vendedor = this.vendedores.find(v => v.codigo_vendedor === data.c_ven);
      const concepto = this.conceptos.find(c => c.codigo === data.concepto);
      
      return data.periodo.toLowerCase().includes(searchStr) ||
             (concepto?.descripcion || '').toLowerCase().includes(searchStr) ||
             (vendedor?.nombre_vendedor || '').toLowerCase().includes(searchStr) ||
             data.c_ven.toLowerCase().includes(searchStr);
    };
    
    // Forzar la actualización del filtro
    this.dataSource.filter = this.dataSource.filter || ' ';
  }

  mostrarTodosLosVendedores(): void {
    this.mostrarTodosVendedores = true;
    this.vendedorControl.setValue('');
    if (this.autocompleteTrigger) {
      this.autocompleteTrigger.openPanel();
    }
  }

  resetForm(): void {
    this.isEditing = false;
    this.editingAjuste = null;
    this.ajusteForm.reset();
    this.vendedorControl.reset();
    // Habilitar el control del vendedor
    this.vendedorControl.enable();
  }

  parseValor(valor: any): number {
    if (typeof valor === 'string') {
      return parseFloat(valor.replace(/,/g, ''));
    }
    return valor;
  }

  eliminarAjuste(element: AjusteValor) {
    if (!element.id) {
      this.notificationService.errorMessage('No se puede eliminar un ajuste sin ID');
      return;
    }

    if (!this.isPeriodoAbierto(element.periodo)) {
      this.notificationService.warningMessage('No se puede eliminar un registro de un periodo cerrado');
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '350px',
      data: { message: '¿Está seguro de que desea eliminar este ajuste?' }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.isLoading = true;
        this.ajusteValoresService.deleteAjuste(element.id!, 'JA')
          .pipe(finalize(() => this.isLoading = false))
          .subscribe({
            next: () => {
              this.notificationService.successMessage('Ajuste eliminado correctamente');
              this.cargarDatos();
            },
            error: (error) => {
              this.notificationService.errorMessage('Error al eliminar el ajuste');
            }
          });
      }
    });
  }

  editarAjuste(ajuste: AjusteValor) {
    if (!this.isPeriodoAbierto(ajuste.periodo)) {
      this.notificationService.warningMessage('No se puede editar un registro de un periodo cerrado');
      return;
    }
    
    this.isEditing = true;
    this.editingAjuste = { ...ajuste }; // Guardar una copia del ajuste que se está editando
    // Buscar el vendedor correspondiente
    const vendedor = this.vendedores.find(v => v.codigo_vendedor === ajuste.c_ven);
    this.ajusteForm.patchValue({
      periodo: ajuste.periodo,
      concepto: ajuste.concepto,
      vendedor: ajuste.c_ven,
      valor: ajuste.valor
    });
    // Establecer el vendedor en el control de vendedor
    if (vendedor) {
      this.vendedorControl.setValue(vendedor, { emitEvent: false });
    }

    // Desactivar el control del vendedor
    this.vendedorControl.disable();
  }

  verDetalles(ajuste: AjusteValor): void {
    this.dialog.open(DetalleAjusteDialogComponent, {
      width: '700px',
      data: {
        ajuste,
        conceptoDescripcion: this.getConceptoDescripcion(ajuste.concepto),
        vendedorNombre: this.getVendedorNombre(ajuste.c_ven)
      }
    });
  }

  getConceptoDescripcion(conceptoId: string): string {

    // Intentar encontrar el concepto por código
    const concepto = this.conceptos.find(c => c.codigo === conceptoId);
    if (concepto) {
      return ` ${concepto.descripcion}`;
    }

    // Si no se encuentra, devolver el código
    return conceptoId;
  }

  getVendedorNombre(codigoVendedor: string): string {
    if (!codigoVendedor || !this.vendedores) {
      return '';
    }

    const vendedor = this.vendedores.find(v => v.codigo_vendedor === codigoVendedor);
    return vendedor ? `${vendedor.nombre_vendedor}` : codigoVendedor;
  }

  onValorInput(event: any): void {
    let value = event.target.value;
    value = value.replace(/[^\d-]/g, '');

    if (value.split('-').length > 2) {
      value = value.replace(/-/g, '');
      if (value.length > 0) {
        value = '-' + value;
      }
    }

    if (value.includes('-') && value.indexOf('-') !== 0) {
      value = '-' + value.replace(/-/g, '');
    }

    const numeroValue = value === '-' ? '' : Number(value);
    this.ajusteForm.get('valor')?.setValue(numeroValue);
  }

  cancelarEdicion(): void {
    this.isEditing = false;
    this.editingAjuste = null;
    this.ajusteForm.reset();
    this.vendedorControl.reset();
    // Asegurarse de que el control del vendedor esté habilitado
    this.vendedorControl.enable();
  }

  private aplicarFiltros() {
    if (!this.dataSource || !this.dataSource.data) {
      return;
    }

    this.dataSource.filterPredicate = (data: AjusteValor, filter: string) => {
      const periodoFilter = this.periodoFiltro;
      const vendedorFilter = this.vendedorFilterControl.value;

      let cumplePeriodo = true;
      let cumpleVendedor = true;

      if (periodoFilter) {
        cumplePeriodo = data.periodo === periodoFilter;
      }

      if (vendedorFilter) {
        const vendedorId = typeof vendedorFilter === 'object' ? vendedorFilter.codigo_vendedor : vendedorFilter;
        cumpleVendedor = data.c_ven === vendedorId;
      }

      return cumplePeriodo && cumpleVendedor;
    };

    // Aplicar el filtro solo si hay datos
    if (this.dataSource.data.length > 0) {
      this.dataSource.filter = 'trigger';

      if (this.dataSource.paginator) {
        this.dataSource.paginator.firstPage();
      }
    }
  }

  limpiarFiltros() {
    this.periodoFiltro = '';
    this.vendedorFilterControl.setValue('');
  }

  mostrarTodosLosVendedoresFilter(): void {
    this.mostrarTodosVendedoresFilter = true;
    this.vendedorFilterControl.setValue('');
  }

  displayVendedor = (vendedor: Vendedor): string => {
    return vendedor ? `${vendedor.codigo_vendedor} - ${vendedor.nombre_vendedor}` : '';
  }

  private cargarVendedores(searchTerm: string = ''): Observable<Vendedor[]> {
    // Si ya tenemos los vendedores cargados y hay término de búsqueda, filtramos localmente
    if (this.vendedoresCargados && this.vendedores.length > 0) {
      return of(this.filtrarVendedores(searchTerm));
    }

    // Si no tenemos vendedores, los cargamos del servidor
    return this.vendedorService.getVendedores('JA').pipe(
      map(vendedores => {
        this.vendedores = vendedores; // Almacenar para uso en filtros
        this.vendedoresCargados = true;
        return this.filtrarVendedores(searchTerm);
      })
    );
  }

  private filtrarVendedores(searchTerm: string): Vendedor[] {
    if (!searchTerm) return this.vendedores;
    
    const termLower = searchTerm.toLowerCase();
    return this.vendedores.filter(vendedor =>
      vendedor.nombre_vendedor.toLowerCase().includes(termLower) ||
      vendedor.codigo_vendedor.toLowerCase().includes(termLower)
    );
  }

  isPeriodoAbierto(periodo: string): boolean {
    return this.periodosAbiertos.some(p => p.periodo === periodo);
  }
}
